import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Heart, Shield, Pill } from "lucide-react";
import { Link } from "react-router-dom";
import { nhsServices, nhsServiceCategories } from "@/data/nhsServices";

export const NHSServicesTab = () => {
  return (
    <div className="space-y-16">
      {/* NHS Header */}
      <div className="bg-gradient-to-br from-[hsl(var(--nhs-blue))]/10 to-[hsl(var(--nhs-blue))]/5 rounded-2xl p-8 md:p-12 border border-[hsl(var(--nhs-blue))]/20">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--nhs-blue))] flex items-center justify-center">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">
              Free NHS Pharmacy Services
            </h3>
            <p className="text-muted-foreground">
              No GP appointment required for many conditions
            </p>
          </div>
        </div>
        <p className="text-muted-foreground leading-relaxed mb-6">
          Through nationally commissioned NHS services, our trained pharmacists can assess, treat and support many common health needs. All NHS services are delivered in line with guidance from the NHS and Community Pharmacy England.
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[
            'Free NHS-funded services',
            'No GP appointment required',
            'Confidential consultations',
            'Fast access to treatment',
            'Experienced pharmacists',
            'Convenient local pharmacy',
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[hsl(var(--nhs-blue))] flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Minor Ailments Service - Featured */}
      <Card className="p-8 bg-gradient-to-br from-[hsl(var(--nhs-blue))]/10 via-background to-[hsl(var(--nhs-blue))]/5 border-[hsl(var(--nhs-blue))]/30 hover:shadow-[var(--shadow-glow)] transition-all duration-300">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--nhs-blue))] flex items-center justify-center flex-shrink-0">
            <Pill className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                NHS Minor Ailments Service
              </h3>
              <span className="bg-[hsl(var(--nhs-blue))]/10 text-[hsl(var(--nhs-blue))] text-xs font-medium px-2 py-1 rounded-full">
                33 Conditions
              </span>
            </div>
            <p className="text-muted-foreground">
              Get free NHS treatment for common conditions like earache, sore throat, skin infections, and more — no GP appointment needed. Our pharmacists can assess and treat you on the spot.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {['Earache', 'Sore Throat', 'Skin Infections', 'UTI', 'Sinusitis', 'Impetigo'].map((condition) => (
                <span key={condition} className="text-xs bg-muted px-2 py-1 rounded-full text-foreground">
                  {condition}
                </span>
              ))}
              <span className="text-xs text-[hsl(var(--nhs-blue))] font-medium">+27 more</span>
            </div>
          </div>
          <Link to="/nhs-minor-ailments-hull" className="flex-shrink-0">
            <Button className="bg-[hsl(var(--nhs-blue))] hover:bg-[hsl(var(--nhs-blue))]/90 text-white group">
              View All Conditions
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </Card>

      {/* Services by Category */}
      {nhsServiceCategories.map((category) => {
        const categoryServices = nhsServices.filter(s => s.category === category.id);
        if (categoryServices.length === 0) return null;
        
        return (
          <div key={category.id} className="space-y-6">
            {/* Category Header */}
            <div className="flex items-center gap-4">
              <span className="text-3xl">{category.icon}</span>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">
                  {category.label}
                </h3>
                <p className="text-muted-foreground text-sm">{category.description}</p>
              </div>
            </div>
            
            {/* Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryServices.map((service) => (
                <Card 
                  key={service.id} 
                  className="p-6 hover:shadow-[var(--shadow-glow)] transition-all duration-300 group bg-gradient-to-b from-card to-background border-border hover:border-[hsl(var(--nhs-blue))]/30"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-lg font-bold text-foreground group-hover:text-[hsl(var(--nhs-blue))] transition-colors">
                      {service.title}
                    </h4>
                    <span className="bg-[hsl(var(--nhs-blue))]/10 text-[hsl(var(--nhs-blue))] text-xs font-medium px-2 py-1 rounded-full">
                      NHS Free
                    </span>
                  </div>
                  <p className="text-muted-foreground mb-4 text-sm line-clamp-3">
                    {service.shortDescription}
                  </p>
                  <Link to={`/nhs-services/${service.id}`} aria-label={`Learn more about ${service.title}`}>
                    <Button variant="outline" className="w-full group/btn hover:bg-[hsl(var(--nhs-blue))]/10 hover:border-[hsl(var(--nhs-blue))]/30">
                      <span>Learn more about {service.title}</span>
                      <ArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {/* What to Expect */}
      <div className="bg-muted/30 rounded-2xl p-8 md:p-12">
        <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
          What to Expect From an NHS Pharmacy Consultation
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[
            { step: '1', title: 'Private Consultation', desc: 'Meet with a trained pharmacist in a confidential setting.' },
            { step: '2', title: 'Clinical Assessment', desc: 'Assessment based on NHS criteria and guidelines.' },
            { step: '3', title: 'Treatment or Advice', desc: 'Receive appropriate treatment or guidance.' },
            { step: '4', title: 'Referral if Needed', desc: 'GP or NHS services referral when required.' },
            { step: '5', title: 'GP Notification', desc: 'Your GP is informed as per NHS guidance.' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--nhs-blue))] flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">{item.step}</span>
              </div>
              <h4 className="font-semibold text-foreground mb-2">{item.title}</h4>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Eligibility */}
      <Card className="p-8 border-[hsl(var(--nhs-blue))]/20 bg-gradient-to-br from-[hsl(var(--nhs-blue))]/5 to-transparent">
        <div className="flex items-start gap-4">
          <Shield className="w-10 h-10 text-[hsl(var(--nhs-blue))] flex-shrink-0" />
          <div>
            <h3 className="text-xl font-bold text-foreground mb-3">Who Can Use NHS Pharmacy Services?</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[hsl(var(--nhs-blue))]" />
                <span>Patients registered with the NHS</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[hsl(var(--nhs-blue))]" />
                <span>Those meeting specific criteria for the service</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[hsl(var(--nhs-blue))]" />
                <span>Anyone wanting faster access to treatment and advice</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[hsl(var(--nhs-blue))]" />
                <span>People who prefer a local pharmacy setting</span>
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              Eligibility varies by service — our team will always check this with you.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
